import React from 'react';
import styled from 'styled-components';
import { fmtTime } from 'TimeOffList/services/format';
import { IconClock } from 'TimeOffList/components/icons';
import { buildTimeOptions } from '../services/time-options';

type Which = 'from' | 'to';

interface Props {
  allDay: boolean;
  fromMin: number;
  toMin: number;
  openDrop: Which | null;
  timeStepMin: number;
  onToggleAllDay: () => void;
  onStep: (which: Which, dir: 1 | -1) => void;
  onToggleDrop: (which: Which) => void;
  onPickTime: (which: Which, value: number) => void;
}

const Stepper: React.FC<{
  label: string;
  value: number;
  which: Which;
  options: ReturnType<typeof buildTimeOptions>;
  open: boolean;
  current: number;
  onStep: (which: Which, dir: 1 | -1) => void;
  onToggleDrop: (which: Which) => void;
  onPickTime: (which: Which, value: number) => void;
}> = ({ label, value, which, options, open, current, onStep, onToggleDrop, onPickTime }) => (
  <Field>
    <span className="lbl">{label}</span>
    <Group>
      <StepBtn onClick={() => onStep(which, -1)}>−</StepBtn>
      <Value onClick={() => onToggleDrop(which)}>{fmtTime(value)}</Value>
      <StepBtn onClick={() => onStep(which, 1)}>+</StepBtn>
    </Group>
    {open && (
      <Drop>
        {options.map(o => (
          <Option key={o.value} $current={o.value === current} onClick={() => onPickTime(which, o.value)}>
            {o.label}
          </Option>
        ))}
      </Drop>
    )}
  </Field>
);

const TimeSection: React.FC<Props> = ({
  allDay, fromMin, toMin, openDrop, timeStepMin,
  onToggleAllDay, onStep, onToggleDrop, onPickTime,
}) => {
  const options = buildTimeOptions(timeStepMin);
  return (
    <Section>
      <Header>
        <SectionLabel>Time</SectionLabel>
        <div className="right">
          <AllDayLabel $on={allDay}>All Day</AllDayLabel>
          <Switch $on={allDay} role="switch" aria-checked={allDay} title="Toggle all-day" onClick={onToggleAllDay}>
            <Knob $on={allDay} />
          </Switch>
        </div>
      </Header>

      {allDay ? (
        <InfoStrip>
          <IconClock size={16} />
          Off for the entire working day
        </InfoStrip>
      ) : (
        <Fields>
          <Stepper label="From" value={fromMin} which="from" options={options} open={openDrop === 'from'} current={fromMin} onStep={onStep} onToggleDrop={onToggleDrop} onPickTime={onPickTime} />
          <Stepper label="To" value={toMin} which="to" options={options} open={openDrop === 'to'} current={toMin} onStep={onStep} onToggleDrop={onToggleDrop} onPickTime={onPickTime} />
        </Fields>
      )}

    </Section>
  );
};

export default TimeSection;

const Section = styled.div``;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  .right { display: flex; align-items: center; gap: 10px; }
`;

const SectionLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-1);
`;

const AllDayLabel = styled.span<{ $on: boolean }>`
  font-size: 13px;
  font-weight: 700;
  color: ${p => (p.$on ? 'var(--acc)' : 'var(--fg-3)')};
`;

const Switch = styled.button<{ $on: boolean }>`
  position: relative;
  width: 48px;
  height: 28px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: background 150ms;
  background: ${p => (p.$on ? 'var(--acc)' : '#C7CDD9')};
`;

const Knob = styled.span<{ $on: boolean }>`
  position: absolute;
  top: 3px;
  left: 3px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 24, 72, 0.35);
  transition: transform 150ms;
  transform: translateX(${p => (p.$on ? '20px' : '0px')});
`;

const InfoStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-radius: var(--r-md);
  background: var(--bg-subtle);
  font-size: 14px;
  color: var(--fg-2);
  font-weight: 600;
`;

const Fields = styled.div`
  display: flex;
  gap: 12px;
`;

const Field = styled.div`
  flex: 1;
  position: relative;
  .lbl { display: block; font-size: 11px; font-weight: 600; color: var(--fg-3); margin-bottom: 5px; }
`;

const Group = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid var(--border-1);
  border-radius: var(--r-md);
  overflow: hidden;
`;

const StepBtn = styled.button`
  width: 40px;
  border: none;
  background: var(--bg-subtle);
  color: var(--fg-2);
  font-size: 18px;
  cursor: pointer;
  font-family: inherit;
  &:hover { background: var(--border-1); }
`;

const Value = styled.button`
  flex: 1;
  border: none;
  background: var(--bg-surface);
  padding: 11px 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--fg-1);
  cursor: pointer;
  font-family: inherit;
  &:hover { background: var(--bg-row-hover); }
`;

const Drop = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 40;
  margin-top: 4px;
  background: var(--bg-surface);
  border: 1px solid var(--border-1);
  border-radius: var(--r-md);
  box-shadow: var(--shadow-md);
  max-height: 200px;
  overflow-y: auto;
`;

const Option = styled.button<{ $current: boolean }>`
  display: block;
  width: 100%;
  border: none;
  text-align: center;
  padding: 9px 0;
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;
  background: ${p => (p.$current ? '#001848' : 'transparent')};
  color: ${p => (p.$current ? '#fff' : '#2C334B')};
  font-weight: ${p => (p.$current ? 700 : 400)};
  &:hover { background: ${p => (p.$current ? '#001848' : 'var(--bg-subtle)')}; }
`;

